import { Location } from 'history';
import { inject, injectable, interfaces } from 'inversify';
import { reaction } from 'mobx';
import { matchPath } from 'react-router-dom';
import { IErrorsService } from '../../../__legacy__/modules/errors/errors.service';
import { UserInfoLoadedEventType } from '../../../__legacy__/modules/user/events/userInfoLoadedEvent';
import { UserSettingsLoadedEventType } from '../../../__legacy__/modules/user/events/userSettingsLoadedEvent';
import { WorkspaceCreatedEvent, WorkspaceCreatedEventType } from '../../../__legacy__/modules/workspaces/events/workspaceCreatedEvent';
import { WorkspaceDecryptionFailedEventType } from '../../../__legacy__/modules/workspaces/events/workspaceDecryptionFailedEvent';
import { WorkspaceDeletedEventType } from '../../../__legacy__/modules/workspaces/events/workspaceDeletedEvent';
import { IWorkspace, IWorkspacesStore } from '../../../__legacy__/modules/workspaces/workspaces.store';
import { Home } from '../../../__legacy__/routes/config/Home';
import { IEventBus } from '../../../__legacy__/services/eventBus.service';
import { browserHistory } from '../../../usehistory';
import { ICurrentWorkspaceStore } from './CurrentWorkspace.store';
import { CurrentWorkspaceChangedEvent } from './events/CurrentWorkspaceChangedEvent';
import { ICurrentWorkspaceNavigator } from './services/navigator/CurrentWorkspaceNavigator';

export interface ICurrentWorkspaceBl {
  selectWorkspace: (id: string) => void;
  workspaceProvidedInUrl: (id: string) => void;
  Initialize: () => void;
}

export namespace ICurrentWorkspaceBl {
  export const $: interfaces.ServiceIdentifier<ICurrentWorkspaceBl> = Symbol('CURRENT_WORKSPACE_BL');
}

@injectable()
export class CurrentWorkspaceBlImpl implements ICurrentWorkspaceBl {
  private workspaceIdFromUrl: string | undefined;

  public constructor(
    @inject(IWorkspacesStore.$) private readonly workspacesStore: IWorkspacesStore,
    @inject(ICurrentWorkspaceStore.$) private readonly store: ICurrentWorkspaceStore,
    @inject(IEventBus.$) private readonly eventBus: IEventBus,
    @inject(IErrorsService.$) private readonly errorsService: IErrorsService,
    @inject(ICurrentWorkspaceNavigator.$) private readonly navigator: ICurrentWorkspaceNavigator
  ) {}

  Initialize = (): void => {
    const workspaceId = matchPath<{ workspaceId: string }>(location.pathname, Home.Path)?.params.workspaceId;
    if (workspaceId) {
      this.workspaceProvidedInUrl(workspaceId);
    }

    reaction(() => browserHistory.location, this.locationChangedListener);
    this.eventBus.addListener(this.userInfoLoadedListener, UserInfoLoadedEventType);
    this.eventBus.addListener(this.userSettingsLoadedListener, UserSettingsLoadedEventType);
    this.eventBus.addListener(this.workspaceDeletedListener, WorkspaceDeletedEventType);
    this.eventBus.addListener(this.workspaceCreatedListener, WorkspaceCreatedEventType);
    this.eventBus.addListener(this.workspaceDecryptionFailedListener, WorkspaceDecryptionFailedEventType);
  };

  public workspaceProvidedInUrl = (id: string): void => {
    if (this.workspacesStore.workspaces.length === 0) {
      this.workspaceIdFromUrl = id;
      return;
    }

    if (this.store.currentWorkspace.id === id) {
      return;
    }
    this.changeCurrentWorkspaceToGivenOrFirstAvailable(id);
  };

  public selectWorkspace = (id: string): void => {
    if (this.store.currentWorkspace.id === id) {
      return;
    }
    const selectedWorkspace = this.workspacesStore.workspaces.find((w) => w.id === id);
    if (!selectedWorkspace || selectedWorkspace.locked) {
      return;
    }
    this.setCurrentWorkspace(selectedWorkspace);
  };

  private locationChangedListener = (location: Location): void => {
    const workspaceId = matchPath<{ workspaceId: string }>(location.pathname, Home.Path)?.params.workspaceId;

    if (!workspaceId) {
      return;
    }
    this.workspaceProvidedInUrl(workspaceId);
  };

  private userInfoLoadedListener = (): void => {
    this.updateCurrentWorkspace();

    if (this.workspaceIdFromUrl && !this.isCurrentWorkspaceSet) {
      const selectedWorkspace = this.workspacesStore.workspaces.find((w) => w.id === this.workspaceIdFromUrl);
      if (selectedWorkspace) {
        this.setCurrentWorkspace(selectedWorkspace);
        return;
      }
      this.errorsService.setUnauthorized();
    }

    if (this.isCurrentWorkspaceAvailable) {
      return;
    }
    if (this.shouldWaitForUserSettingsToBeSet) {
      return;
    }

    this.changeCurrentWorkspaceToPreferredOrFirstAvailable();
  };

  private userSettingsLoadedListener = (): void => {
    if (this.workspacesStore.workspaces.length === 0 || this.workspaceIdFromUrl || this.isCurrentWorkspaceSet) {
      return;
    }
    this.changeCurrentWorkspaceToPreferredOrFirstAvailable();
  };

  private workspaceDeletedListener = (): void => {
    if (this.isCurrentWorkspaceAvailable) {
      return;
    }
    this.setCurrentWorkspaceToFirstAvailable();
  };

  private workspaceCreatedListener = (event: WorkspaceCreatedEvent): void => {
    this.changeCurrentWorkspaceToGivenOrFirstAvailable(event.newWorkspaceId);
  };

  private workspaceDecryptionFailedListener = (): void => {
    const firstAvailableWithoutEncryption = this.workspacesStore.workspaces.find((w) => !w.encryption.encrypted);
    if (firstAvailableWithoutEncryption) {
      this.setCurrentWorkspace(firstAvailableWithoutEncryption);
    }
  };

  private get isCurrentWorkspaceAvailable(): boolean {
    return !!this.workspacesStore.workspaces.find((w) => w.id === this.store.currentWorkspace.id);
  }

  private get isCurrentWorkspaceSet(): boolean {
    return !!this.store.currentWorkspace.id;
  }

  private get isPreferredWorkspaceSet(): boolean {
    return !!this.workspacesStore.preferredWorkspaceId;
  }

  private get shouldWaitForUserSettingsToBeSet(): boolean {
    return !this.isCurrentWorkspaceSet && !this.isPreferredWorkspaceSet;
  }

  private changeCurrentWorkspaceToPreferredOrFirstAvailable = (): void => {
    this.changeCurrentWorkspaceToGivenOrFirstAvailable(this.workspacesStore.preferredWorkspaceId);
  };

  private changeCurrentWorkspaceToGivenOrFirstAvailable = (workspaceId: string | undefined): void => {
    if (workspaceId) {
      const selectedWorkspace = this.workspacesStore.workspaces.find((w) => w.id === workspaceId);
      if (selectedWorkspace) {
        this.setCurrentWorkspace(selectedWorkspace);
        return;
      }
    }

    this.setCurrentWorkspaceToFirstAvailable();
  };

  private setCurrentWorkspaceToFirstAvailable = (): void => {
    const firstAvailableWorkspace = this.workspacesStore.workspaces[0];
    this.setCurrentWorkspace(firstAvailableWorkspace);
  };

  private setCurrentWorkspace(workspace: IWorkspace): void {
    const isInitialWorkspaceSet = !this.store.currentWorkspace.id;

    this.store.setCurrentWorkspace(workspace);

    if (isInitialWorkspaceSet) {
      this.eventBus.sendEvent(new CurrentWorkspaceChangedEvent(false));
    } else {
      const thereWillBeNavigation = this.navigator.navigateOrReplaceWith(workspace.id);
      this.eventBus.sendEvent(new CurrentWorkspaceChangedEvent(thereWillBeNavigation));
    }
  }

  private updateCurrentWorkspace = (): void => {
    if (!this.isCurrentWorkspaceAvailable) {
      return;
    }
    const updatedCurrentWorkspace = this.workspacesStore.workspaces.find((w) => w.id === this.store.currentWorkspace.id);
    if (updatedCurrentWorkspace) {
      this.store.setCurrentWorkspace(updatedCurrentWorkspace);
    }
  };
}
